import { Box, Flex } from '@chakra-ui/react'
import React, { createContext, type FunctionComponent, type ReactNode } from 'react'
import { useContext, useMemo, useState } from 'react'

const CarouselContext = createContext<any>(null)

const CarouselProvider = ({ children, items, itemsToShow = 3, startOnEnd = false }) => {
  const maxIndex = items.length - itemsToShow
  const [currentIndex, setCurrentIndex] = useState(startOnEnd ? (maxIndex >= 0 ? maxIndex : 0) : 0)

  const contextValue = useMemo(
    () => ({
      items,
      currentIndex,
      itemsToShow,
      goToNext: () => setCurrentIndex(prev => (prev < maxIndex ? prev + 1 : prev)),
      goToPrev: () => setCurrentIndex(prev => (prev > 0 ? prev - 1 : prev)),
      hasNext: currentIndex < items.length - itemsToShow,
      hasPrev: currentIndex > 0,
    }),
    [currentIndex, itemsToShow, items, items.length]
  )

  return <CarouselContext.Provider value={contextValue}>{children}</CarouselContext.Provider>
}

const CarouselChildrenWrapper: FunctionComponent<{ children: ReactNode | ((props) => ReactNode) }> = ({ children }) => {
  const context = useContext(CarouselContext)
  if (!context) throw new Error('CarouselChildrenWrapper must be used within a Carousel')

  if (typeof children === 'function') {
    return <>{children(context)}</>
  }

  return <>{children}</>
}

interface CarouselProps {
  items: any[]
  itemsToShow?: number
  startOnEnd?: boolean
  children: ReactNode
}

const CarouselSlides: FunctionComponent = () => {
  const context = useContext(CarouselContext)
  if (!context) throw new Error('CarouselSlides must be used within a Carousel')
  const { currentIndex, itemsToShow, items } = context

  const flex = `0 0 ${100 / itemsToShow}%`
  const translateX = currentIndex === 0 ? 0 : -(currentIndex * (100 / itemsToShow))

  return (
    <Box position='relative' width='full' overflow='hidden'>
      <Flex transform={`translateX(${translateX}%)`} transition='transform 0.5s ease'>
        {items.map((child, index) => (
          <Box key={index} flex={flex}>
            {child}
          </Box>
        ))}
      </Flex>
    </Box>
  )
}

const CarouselControlPrev: FunctionComponent<{ children: ReactNode | ((props) => ReactNode) }> = ({ children }) => {
  const context = useContext(CarouselContext)
  if (!context) throw new Error('CarouselControlPrev must be used within a Carousel')

  const child = React.Children.only(children)
  return React.cloneElement(child as React.ReactElement<any>, { onClick: context.goToPrev, disabled: !context.hasPrev })
}

const CarouselControlNext: FunctionComponent<{ children: ReactNode | ((props) => ReactNode) }> = ({ children }) => {
  const context = useContext(CarouselContext)
  if (!context) throw new Error('CarouselControlNext must be used within a Carousel')
  const child = React.Children.only(children)
  return React.cloneElement(child as React.ReactElement<any>, { onClick: context.goToNext, disabled: !context.hasNext })
}

const Carousel: FunctionComponent<CarouselProps> & {
  Slides: typeof CarouselSlides
  ControlPrev: typeof CarouselControlPrev
  ControlNext: typeof CarouselControlNext
} = ({ items, itemsToShow = 3, startOnEnd = false, children }) => {
  return (
    <CarouselProvider items={items} itemsToShow={itemsToShow} startOnEnd={startOnEnd}>
      <CarouselChildrenWrapper>{children}</CarouselChildrenWrapper>
    </CarouselProvider>
  )
}

Carousel.Slides = CarouselSlides
Carousel.ControlPrev = CarouselControlPrev
Carousel.ControlNext = CarouselControlNext

export default Carousel
